"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetStartedContactCta() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: subheadRef,
    isVisible: subheadVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  return <section className="relative bg-[hsl(210,12%,18%)] py-20 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-2xl mx-auto">
        <div ref={headlineRef} className={`text-center mb-4 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-[hsl(0,0%,96%)]">
            Get Started
          </h2>
        </div>

        <div ref={subheadRef} className={`text-center mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-100 ${subheadVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="text-lg text-[hsl(0,0%,96%)]/80">
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <FormProvider className="space-y-6" formId="contact-cta-form" sectionName="get-started-contact-cta" defaultValues={{
        name: "",
        email: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Send Message" successMessage="Thank you! We'll get back to you soon.">
          <ContactForm />
        </FormProvider>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: nameRef,
    isVisible: nameVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: emailRef,
    isVisible: emailVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: messageRef,
    isVisible: messageVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-[hsl(210,78%,50%)]/10 border border-[hsl(210,78%,50%)] rounded-lg text-center">
        <p className="text-[hsl(0,0%,96%)] text-lg font-semibold mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="bg-transparent border-[hsl(210,78%,50%)] text-[hsl(210,78%,50%)] hover:bg-[hsl(210,78%,50%)]/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div ref={nameRef} className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${nameVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(0,0%,96%)]">Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" {...field} className="bg-[hsl(210,12%,18%)] border-[hsl(210,10%,86%)] text-[hsl(0,0%,96%)] placeholder:text-[hsl(0,0%,96%)]/50 focus-visible:border-[hsl(210,78%,50%)] focus-visible:ring-[hsl(210,78%,50%)]/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div ref={emailRef} className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-100 ${emailVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(0,0%,96%)]">Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} className="bg-[hsl(210,12%,18%)] border-[hsl(210,10%,86%)] text-[hsl(0,0%,96%)] placeholder:text-[hsl(0,0%,96%)]/50 focus-visible:border-[hsl(210,78%,50%)] focus-visible:ring-[hsl(210,78%,50%)]/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div ref={messageRef} className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-200 ${messageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(0,0%,96%)]">Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Your message..." {...field} className="bg-[hsl(210,12%,18%)] border-[hsl(210,10%,86%)] text-[hsl(0,0%,96%)] placeholder:text-[hsl(0,0%,96%)]/50 focus-visible:border-[hsl(210,78%,50%)] focus-visible:ring-[hsl(210,78%,50%)]/50 min-h-32" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg">
          <p className="text-red-400">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-[hsl(210,78%,50%)] text-white hover:bg-[hsl(210,73%,55%)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.08)] hover:scale-[1.02] active:bg-[hsl(210,78%,45%)] active:scale-100 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus-visible:ring-[hsl(210,78%,50%)]/40">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}